/**
 * Drug-schedule helpers — pure functions, no state, safe to import from
 * anywhere in the section. Split out because they're used by BOTH the
 * middle-column table (`DrugSchedule.js`) and the right-rail editor
 * (`DrugScheduleEditor.js`).
 */

/**
 * Prod status casing is "Active" / "Completed" / "Expired" (capitalised).
 * Normalise for comparison so a rogue "active" from another source
 * doesn't fall through the render switch.
 */
export const STATUS = {
  ACTIVE:    "Active",
  COMPLETED: "Completed",
  EXPIRED:   "Expired",
};
export const normaliseStatus = (s) => {
  const v = String(s || "").trim().toLowerCase();
  if (v === "active")    return STATUS.ACTIVE;
  if (v === "completed") return STATUS.COMPLETED;
  if (v === "expired")   return STATUS.EXPIRED;
  return "";
};

/** "2026-05-02" → "02 May 2026". Empty / bad input → "—". */
export const formatDateShort = (iso) => {
  if (!iso || typeof iso !== "string") return "—";
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  const d = new Date(Date.UTC(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])));
  if (isNaN(d.getTime())) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${String(d.getUTCDate()).padStart(2, "0")} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

/** Today as "YYYY-MM-DD" in the browser's timezone. */
export const todayISO = () => {
  const d = new Date();
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
};

/**
 * "YYYY-MM-DD" + N days → "YYYY-MM-DD". UTC math so we don't slip a
 * day across the browser's local DST boundary.
 */
export const addDaysISO = (iso, days) => {
  if (!iso || !Number.isFinite(days)) return "";
  const parts = iso.split("-");
  if (parts.length !== 3) return "";
  const d = new Date(Date.UTC(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])));
  d.setUTCDate(d.getUTCDate() + Number(days));
  return [
    d.getUTCFullYear(),
    String(d.getUTCMonth() + 1).padStart(2, "0"),
    String(d.getUTCDate()).padStart(2, "0"),
  ].join("-");
};

/** Compare two "YYYY-MM-DD" lexicographically. Safe because zero-padded. */
export const isoBefore = (a, b) => (a && b && a < b);

/**
 * Derive the display state of ONE dose for the View-schedule card.
 *   Taken     — actualdate present (regardless of schedule status)
 *   Missed    — schedule Active AND plannedDate < today AND no actual
 *   Upcoming  — schedule Active AND plannedDate >= today AND no actual
 *   (for terminal-state schedules with un-taken doses, we still show
 *   Missed if the planned date has passed; else Upcoming — but with the
 *   schedule terminal a strict interpretation would show them as
 *   Missed. Following the mockup we render Missed only in the strict
 *   past-date case, else — for future dates on terminal schedules —
 *   we render Upcoming to preserve the planned intent.)
 */
export const doseDisplayStatus = (dose, planStatus) => {
  const actual = String(dose.actualdate || "").trim();
  if (actual) return "Taken";
  const planned = String(dose.planneddate || "").trim();
  const today = todayISO();
  if (isoBefore(planned, today)) return "Missed";
  return "Upcoming";
  // planStatus is unused in the current spec but reserved so the caller
  // can pass it — a future rule might gate the Missed/Upcoming split on
  // whether the schedule itself is still Active.
};

/**
 * Locate the "next" dose on an Active plan — first dose with no
 * actualdate. Returns {index, doseNo, plannedDate} or null when every
 * dose is taken (i.e. the plan is effectively completed).
 */
export const nextDoseFor = (plan) => {
  if (!plan || !Array.isArray(plan.doses)) return null;
  for (let i = 0; i < plan.doses.length; i += 1) {
    const d = plan.doses[i];
    if (!String(d.actualdate || "").trim()) {
      return {
        index:       i,
        doseNo:      Number(d.doseno) || (i + 1),
        plannedDate: d.planneddate || "",
      };
    }
  }
  return null;
};

/**
 * Drug name for a plan. Prod stores it per-dose on `dosename`; the
 * first dose is authoritative (all doses in a plan share the drug).
 */
export const drugNameOf = (plan) => {
  if (!plan || !Array.isArray(plan.doses) || plan.doses.length === 0) return "";
  return plan.doses[0].dosename || "";
};

/**
 * Client-side fuzzy filter over the drug catalog. Matches when every
 * query token (whitespace-split) is a substring of the drugname
 * (case-insensitive). Empty query returns the full list.
 */
export const filterDrugs = (drugs, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return drugs || [];
  const tokens = q.split(/\s+/).filter(Boolean);
  return (drugs || []).filter((d) => {
    const hay = String(d.drugname || "").toLowerCase();
    return tokens.every((t) => hay.includes(t));
  });
};

/**
 * Best-effort dose-array expansion from AI atoms. The transcript agent
 * emits {drug_name, start_date, num_doses, interval_value, interval_unit}
 * without the doses[] array — that's filled in by the Go writeDrugSchedule
 * adapter on Save. When we need a doses[] client-side (peach staged row,
 * pre-filling the Add sheet from an AI proposal), we mirror the adapter's
 * math here so the UI stays coherent before commit.
 */
export const buildDosesFromAtoms = (p) => {
  const drug = String((p && p.drug_name) || "").trim();
  const start = String((p && p.start_date) || "").slice(0, 10);
  const n = Math.max(1, Number(p && p.num_doses) || 1);
  const gap = Math.max(1, Number(p && p.interval_value) || 1);
  const unit = String((p && p.interval_unit) || "days").toLowerCase();
  const dayFactor = unit === "weeks" ? 7 : unit === "months" ? 30 : 1;
  const startDate = start ? new Date(start) : new Date();
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const d = new Date(startDate.getTime());
    d.setDate(d.getDate() + i * gap * dayFactor);
    out.push({
      doseno: i + 1,
      dosename: drug,
      planneddate: d.toISOString().slice(0, 10),
      rescheduledate: "",
      actualdate: "",
      nextdosefrequency: { unit: "days", value: i < n - 1 ? String(gap * dayFactor) : "" },
    });
  }
  return out;
};

/**
 * Turn one captured drug_schedule item into a "plan-shape" object the
 * middle-column table can render (and DS_V2_EDIT_CAPTURED can pass to
 * DrugScheduleEditor's SheetStack). If the payload carries a full
 * doses[] array we use it verbatim; otherwise we synthesise one from
 * atoms so AI-proposed captures still show a coherent plan.
 */
export const stagedAddToPlan = (item) => {
  // Table-overlay rule (matches Addictions / Anthropometric / PD):
  // prefer the LAST-COMMITTED snapshot so an uncommitted AI delta
  // doesn't leak into the row — the reviewer's accept in the sheet
  // editor is what moves values from Current → LastCommitted and
  // makes them visible. For an item that was committed and then
  // re-emitted by the AI (Shift-4 reopen), LastCommitted preserves
  // the reviewer's typed values. Falls back to current when LC isn't
  // populated yet (first AI emission on a pending item that the
  // caller still wants rendered — unusual for the table but kept
  // for compat).
  const p = (item && (item.lastCommitted || item.current)) || {};
  const doses = Array.isArray(p.doses) && p.doses.length > 0
    ? p.doses
    : buildDosesFromAtoms(p);
  const op = (item && item.operation) || "add";
  const isRejected = item && item.state === "rejected" && !item.dbRecordRef && item.lastCommitted;
  return {
    id:        `staged:${item.id}`,
    status:    p.status || STATUS.ACTIVE,
    doses,
    createdby: p.createdby || "AI transcript",
    createdAt: item.createdAt || new Date().toISOString(),
    _captured:          item,
    _isStaged:          true,
    _isAi:              item.source === "ai",
    _isRejected:        !!isRejected,
    _capturedOperation: op,
  };
};
